import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, ExternalLink, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  priority: string;
  related_id?: string;
}

const BigNotificationOverlay = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeNotification, setActiveNotification] = useState<Notification | null>(null);

  useEffect(() => {
    if (!user) return;
    let isSubscribed = true;
    let lastCheckedAt = new Date().toISOString();
    const displayedIds = new Set<string>();

    // HTTP Polling for 1M user scale (replaces WebSocket)
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible' && isSubscribed) {
        supabase
          .from('notifications')
          .select('*')
          .eq('user_id', user.id)
          .eq('priority', 'high')
          .gt('created_at', lastCheckedAt)
          .order('created_at', { ascending: false })
          .limit(1)
          .then(({ data }) => {
            if (!isSubscribed) return;
            if (data && data.length > 0) {
              const newNotif = data[0] as Notification;
              if (!displayedIds.has(newNotif.id)) {
                setActiveNotification(newNotif);
                displayedIds.add(newNotif.id);
                // Play a subtle sound if possible or rely on visual
                if ('vibrate' in navigator) {
                  navigator.vibrate([100, 50, 100]);
                }
              }
              lastCheckedAt = newNotif.created_at || new Date().toISOString();
            } else {
              // Advance the checking window with a slight overlap (5s) for clock skew safety
              lastCheckedAt = new Date(Date.now() - 5000).toISOString(); 
            }
          });
      }
    }, 15000); // 15s polling for high priority

    return () => { 
      isSubscribed = false;
      clearInterval(interval); 
    };
  }, [user]);

  const handleAction = () => {
    if (!activeNotification) return;
    
    // Custom routing based on notification type
    if (activeNotification.type === 'booking' && activeNotification.related_id) {
       // Check if worker or user
       const isWorkerPath = window.location.pathname.includes('/worker');
       if (isWorkerPath) {
         navigate('/worker/dashboard');
       } else {
         navigate('/bookings');
       }
    } else if (activeNotification.type === 'auto_assign_failed') {
      navigate('/admin/bookings');
    }
    
    setActiveNotification(null);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'error': return <AlertCircle className="h-8 w-8 text-destructive" />;
      case 'success': return <CheckCircle2 className="h-8 w-8 text-success" />;
      default: return <Bell className="h-8 w-8 text-primary" />;
    }
  };

  return (
    <AnimatePresence>
      {activeNotification && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="w-full max-w-sm bg-card border-2 border-primary/20 shadow-2xl rounded-3xl overflow-hidden p-6 relative"
          >
            <button 
              onClick={() => setActiveNotification(null)}
              className="absolute top-4 right-4 p-2 hover:bg-secondary rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </button>

            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-primary/10 rounded-full">
                {getIcon(activeNotification.type)}
              </div>
              
              <div className="space-y-2">
                <h2 className="text-xl font-bold tracking-tight">{activeNotification.title}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {activeNotification.message}
                </p>
              </div>

              <div className="flex flex-col w-full gap-2 pt-2">
                <Button onClick={handleAction} className="w-full h-12 rounded-2xl text-base font-semibold gap-2 shadow-lg shadow-primary/20">
                  <ExternalLink className="h-4 w-4" /> View Details
                </Button>
                <Button variant="ghost" onClick={() => setActiveNotification(null)} className="w-full h-11 rounded-2xl text-sm">
                  Dismiss
                </Button>
              </div>
            </div>
            
            {/* Animated background pulse */}
            <motion.div 
              animate={{ opacity: [0.1, 0.3, 0.1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute inset-0 bg-primary/5 pointer-events-none"
            />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default BigNotificationOverlay;
